import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Printer, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

// No more than 10 tabs a sheet (room to write a name after a few beers), shared evenly
// across sheets: 15 songs print as 8 + 7 (curation spec §3.7)
const MAX_TABS = 10;
const WARN_OVER = 15;

const splitIntoSheets = (songs) => {
  const count = Math.max(1, Math.ceil(songs.length / MAX_TABS));
  const per = Math.ceil(songs.length / count);
  return Array.from({ length: count }, (_, i) => songs.slice(i * per, (i + 1) * per));
};

// gig_date arrives as 'YYYY-MM-DD'; read it as a local date
const formatDate = (d) => d
  ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })
  : '';

// The printable tear-off request sheet: the audience tears off a song, writes their name on
// the back and drops it in the hat. Printed at home before the gig, A4 portrait.
function RequestSheet() {
  const { gigUrl } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const pagesRef = useRef(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/gigs/${gigUrl}/request-sheet`)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.status === 404 ? 'Gig not found' : 'Couldn\'t load the request sheet. It needs signal.'));
  }, [gigUrl]);

  // Shrink any title (then artist) that doesn't fit its tab
  useLayoutEffect(() => {
    if (!pagesRef.current) return;
    pagesRef.current.querySelectorAll('.rs-text').forEach(box => {
      for (const cls of ['rs-title', 'rs-artist']) {
        const el = box.querySelector(`.${cls}`);
        if (!el) continue;
        let size = parseFloat(getComputedStyle(el).fontSize);
        while (box.scrollHeight > box.clientHeight + 1 && size > 8) {
          size -= 0.5;
          el.style.fontSize = `${size}px`;
        }
      }
    });
  }, [data]);

  if (error) return <div style={{ padding: '3rem', color: '#c53030', fontFamily: 'system-ui' }}>{error}</div>;
  if (!data) return (
    <div style={{ minHeight: '100dvh', background: '#0f0f0f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Loader2 size={32} style={{ color: '#888', animation: 'gr-spin 0.8s linear infinite' }} />
    </div>
  );

  const accent = data.primary_color || '#667eea';
  const bandName = data.band_name || data.gig_name;
  const gigLine = [data.venue_name, formatDate(data.gig_date)].filter(Boolean).join(' · ');
  const sheets = splitIntoSheets(data.songs);
  const noun = (n, one, many) => `${n} ${n === 1 ? one : many}`;

  return (
    <>
      <style>{`
        @page { size: A4 portrait; margin: 0; }
        body { margin: 0; }
        .rs { --accent: ${accent}; --ink: #1d1d1f; --muted: #6e6e73; --cut: #9a9aa0;
              font-family: "Segoe UI", system-ui, -apple-system, Helvetica, Arial, sans-serif; color: var(--ink); }
        .rs * { box-sizing: border-box; }
        .rs-page { width: 210mm; height: 297mm; margin: 0 auto 10mm; background: #fff; position: relative;
                   padding: 8mm 7mm 6mm; display: flex; flex-direction: column; overflow: hidden;
                   box-shadow: 0 2px 12px rgba(0,0,0,.15); break-after: page; }
        .rs-page:last-child { break-after: auto; }
        @media screen { body { background: #e5e7eb; } }
        @media print {
          body { background: #fff; }
          .no-print { display: none !important; }
          .rs-pages { padding: 0 !important; }
          .rs-page { margin: 0; box-shadow: none; }
          .rs { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        .rs-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 6mm; }
        .rs-logo { max-height: 14mm; max-width: 60mm; object-fit: contain; display: block; margin-bottom: 2mm; }
        .rs-band { font-size: 11pt; letter-spacing: .25em; text-transform: uppercase; color: var(--accent); font-weight: 700; }
        .rs h1 { font-size: 34pt; line-height: 1; margin: 2mm 0 1mm; font-weight: 800; }
        .rs-gig { font-size: 12pt; color: var(--muted); }
        .rs-qr { width: 32mm; text-align: center; font-size: 8pt; color: var(--muted); flex-shrink: 0; }
        .rs-qr img { width: 32mm; height: 32mm; display: block; margin-bottom: 1mm; }
        .rs-steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 5mm; margin: 7mm 0 5mm; }
        .rs-step { border: 0.4mm solid #e2e2e8; border-radius: 3mm; padding: 5mm 4mm; text-align: center; }
        .rs-step .n { width: 11mm; height: 11mm; border-radius: 50%; background: var(--accent); color: #fff; font-weight: 800;
                      font-size: 15pt; display: grid; place-items: center; margin: 0 auto 3mm; }
        .rs-step .t { font-size: 13pt; font-weight: 700; }
        .rs-step .s { font-size: 9.5pt; color: var(--muted); margin-top: 1mm; }
        .rs-draw { text-align: center; font-size: 12pt; margin-bottom: 4mm; }
        .rs-draw b { color: var(--accent); }
        .rs-spacer { flex: 1; }
        .rs-tearline { position: relative; border-top: 0.4mm dashed var(--cut); margin: 0 -2mm; }
        .rs-tearline span { position: absolute; left: 4mm; top: -3.2mm; background: #fff; padding: 0 1.5mm; font-size: 9pt; color: var(--muted); }
        /* 5 mm side margins: most home printers can't print right to the edge */
        .rs-tabs { display: flex; height: 135mm; margin: 0 -2mm -6mm; }
        .rs-tab { flex: 0 0 calc(100% / var(--tabs)); position: relative; border-left: 0.3mm dashed var(--cut);
                  display: flex; flex-direction: column; align-items: center; justify-content: flex-end; padding: 0 0 5mm; }
        .rs-tab:first-child { border-left: none; }
        /* Reads bottom to top, starting at the bottom edge: a ragged tear at the top only loses blank space */
        .rs-text { writing-mode: vertical-rl; transform: rotate(180deg); height: 104mm; max-width: 100%; display: flex; flex-direction: column;
                   justify-content: flex-start; align-items: flex-start; gap: 1.2mm; white-space: nowrap; overflow: hidden; }
        .rs-title { font-weight: 700; font-size: 16pt; }
        .rs-artist { font-size: 10.5pt; color: var(--muted); }
        .rs-back { position: absolute; top: 6mm; font-size: 8pt; color: var(--accent); writing-mode: vertical-rl; transform: rotate(180deg); }
        .rs-count { position: absolute; right: 7mm; top: 4mm; font-size: 7pt; color: #b0b0b6; }
      `}</style>

      <div className="rs">
        {/* Toolbar and notes: screen only */}
        <div className="no-print" style={{ position: 'sticky', top: 0, zIndex: 10, background: '#1a1a2e', color: '#fff', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          {/* Opened in its own tab: close it, or if the browser won't allow that, go to the gigs */}
          <button onClick={() => { window.close(); setTimeout(() => window.location.assign('/dashboard/gigs'), 300); }} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.75)', cursor: 'pointer', fontSize: '0.9rem', padding: 0 }}>✕ Close</button>
          <span style={{ fontWeight: 600, fontSize: '0.95rem', flex: 1, minWidth: '10rem' }}>
            Request sheet: {noun(data.songs.length, 'song', 'songs')} on {noun(sheets.length, 'sheet', 'sheets')}
          </span>
          <button onClick={() => window.print()} disabled={data.songs.length === 0}
            style={{ padding: '0.5rem 1.1rem', background: accent, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.35rem', opacity: data.songs.length ? 1 : 0.5 }}>
            <Printer size={15} /> Print
          </button>
        </div>
        <div className="no-print" style={{ maxWidth: '210mm', margin: '1rem auto', padding: '0 1rem', fontFamily: 'system-ui', fontSize: '0.9rem', lineHeight: 1.5, color: '#333' }}>
          {data.songs.length === 0 ? (
            <p style={{ color: '#c53030', fontWeight: 600 }}>There are no songs on this gig's request list, so there's nothing to print. Check the Request list in the Set List Builder.</p>
          ) : (
            <p style={{ margin: '0 0 0.5rem' }}>Print on A4 at 100% ("Actual size", not "Fit to page"). Cut along the dashed lines between the songs, up to the line across the sheet.</p>
          )}
          {data.songs.length > WARN_OVER && (
            <p style={{ margin: '0 0 0.5rem', padding: '0.6rem 0.8rem', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px' }}>
              <b>{data.songs.length} songs = {sheets.length} sheets.</b> That's a lot of paper for one hat. To print fewer, set the Request list to <b>Hand-picked</b> in the Set List Builder and tick your favourites.
            </p>
          )}
          {data.capped && (
            <p style={{ margin: '0 0 0.5rem' }}>The free plan prints the first {data.cap} of your {data.total} songs, the same as the audience page shows.</p>
          )}
          {!data.has_request_block && data.songs.length > 0 && (
            <p style={{ margin: '0 0 0.5rem', color: '#6e6e73' }}>This gig's set list has no request block yet.</p>
          )}
        </div>

        <div className="rs-pages" ref={pagesRef} style={{ padding: '0 0 1rem' }}>
          {data.songs.length > 0 && sheets.map((sheet, i) => (
            <section className="rs-page" key={i}>
              {sheets.length > 1 && <div className="rs-count">Sheet {i + 1} of {sheets.length}</div>}
              <div className="rs-head">
                <div style={{ minWidth: 0 }}>
                  {data.logo_url && <img className="rs-logo" src={data.logo_url} alt="" />}
                  <div className="rs-band">{bandName}</div>
                  <h1>Request a song</h1>
                  {gigLine && <div className="rs-gig">{gigLine}</div>}
                </div>
                <div className="rs-qr">
                  <img src={data.qr_data_uri} alt="QR code for the request page" />
                  Got signal? Scan to request online
                </div>
              </div>
              <div className="rs-steps">
                <div className="rs-step"><div className="n">1</div><div className="t">Tear off a song</div><div className="s">One tab per song, so be quick!</div></div>
                <div className="rs-step"><div className="n">2</div><div className="t">Write your name</div><div className="s">on the back of the tab</div></div>
                <div className="rs-step"><div className="n">3</div><div className="t">Drop it in the hat</div><div className="s">by the stage</div></div>
              </div>
              <div className="rs-draw">We'll <b>draw requests from the hat</b> during the gig.</div>
              <div className="rs-spacer" />
              <div className="rs-tearline"><span>✂ Cut between the songs up to here</span></div>
              <div className="rs-tabs" style={{ '--tabs': Math.max(sheet.length, 5) }}>
                {sheet.map(song => (
                  <div className="rs-tab" key={song.id}>
                    <div className="rs-text">
                      <span className="rs-title">{song.rusty ? '🤞 ' : ''}{song.title}</span>
                      {song.artist_original && <span className="rs-artist">{song.artist_original}</span>}
                    </div>
                    <span className="rs-back">✎ name on back</span>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </>
  );
}

export default RequestSheet;
